import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { locale, t, type Locale } from '../i18n.js';
import { theme, type Theme } from '../theme.js';
import { formatDateTime, formatSize } from '../format.js';
import { onPickAvatar, onSaveAlias } from '../actions.js';
import { me } from '../state.js';
import { Action, Avatar } from './parts.js';
import { IconCamera, IconCheck } from './icons.js';

/**
 * **Moi** — *où j'en suis ?*
 *
 * ### Se nommer, depuis le 2026-08-16
 *
 * Cet écran n'a longtemps rien porté d'éditable, et disait pourquoi : le profil,
 * l'avatar et la liste des appareils demandaient les routes d'`api.md` §7, qui
 * n'existaient pas. Les deux premières existent (incrément 4.8), et ce qu'elles
 * ferment n'est pas un confort : `Alias` n'était écrit que par la route
 * d'administration, **à la création du compte**. Un nom faux y était donc
 * définitif — et pas seulement pour soi, puisque le nom est la seule chose qui
 * désigne une personne dans un groupe.
 *
 * La liste des appareils, elle, attend toujours ses routes.
 *
 * ### Pourquoi l'avatar est ici, et pas dans un réglage
 *
 * C'est le vecteur le plus large du système — une image qui s'affiche chez tous
 * les membres de tous les groupes. Le mettre à côté du nom dit ce qu'il est : la
 * **même** décision que se nommer, prise deux fois.
 */
export function MeView(): JSX.Element | null {
  const profile = me.value;
  if (!profile) return null;

  const { usedBytes, quotaBytes, deletedBytes } = profile.storage;
  const ratio = quotaBytes > 0 ? Math.min(1, usedBytes / quotaBytes) : 0;

  return (
    <div class="stack" data-testid="me">
      <section class="panel">
        <h3>{t('me.identity')}</h3>
        <div class="identity">
          <AvatarPicker />
          <div class="grow">
            <AliasField />
            {/* Le `uid` est l'identifiant **applicatif**, résolu par le trigger
                `pre-token`. Il n'est pas secret — il ne dit rien de l'adresse ni
                du compte Cognito (D13) — et c'est le seul qu'on demandera de
                recopier au support. */}
            <div class="mono muted">{profile.uid}</div>
            {profile.isAdmin ? <div class="card-meta">{t('me.admin')}</div> : null}
          </div>
        </div>
      </section>

      <section class="panel">
        <h3>{t('me.storage')}</h3>
        {/* `<progress>` plutôt qu'une barre fabriquée : la valeur est portée par
            l'élément, donc lue par les technologies d'assistance sans aucun
            `aria-*`, et la largeur est calculée par le navigateur — ce qui évite
            l'attribut `style` que la CSP refuse. */}
        <progress class="gauge" max={100} value={Math.round(ratio * 100)} aria-label={t('me.storage')} />
        <p class="card-meta">
          {t('me.used', { used: formatSize(usedBytes), quota: formatSize(quotaBytes) })}
          {deletedBytes > 0 ? ` · ${t('me.deleted', { size: formatSize(deletedBytes) })}` : ''}
        </p>
      </section>

      <section class="panel">
        <h3>{t('me.preferences')}</h3>
        <div class="setting">
          <label for="theme-select">{t('me.theme')}</label>
          <select
            id="theme-select"
            class="input"
            data-testid="theme-select"
            value={theme.value}
            onChange={(ev) => (theme.value = (ev.currentTarget as HTMLSelectElement).value as Theme)}
          >
            <option value="system">{t('header.theme.system')}</option>
            <option value="light">{t('header.theme.light')}</option>
            <option value="dark">{t('header.theme.dark')}</option>
          </select>
        </div>
        <div class="setting">
          <label for="locale-select">{t('me.language')}</label>
          <select
            id="locale-select"
            class="input"
            data-testid="locale-select"
            value={locale.value}
            onChange={(ev) =>
              (locale.value = (ev.currentTarget as HTMLSelectElement).value as Locale)
            }
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </div>
        <div class="setting">
          <span>{t('me.serverTime')}</span>
          <span class="muted">{formatDateTime(profile.serverTime)}</span>
        </div>
      </section>

      <p class="card-meta" data-testid="me-pending">
        {t('me.devicesSoon')}
      </p>
    </div>
  );
}

/**
 * Le nom affiché — **un champ, un bouton, et rien tant qu'on n'a rien changé**.
 *
 * Le bouton n'apparaît que lorsque la valeur diffère de celle enregistrée : un
 * *Enregistrer* permanent invite à appuyer pour rien, et chaque appui est une
 * écriture.
 *
 * Vider le champ est un geste **légitime** — se dénommer — et non une erreur de
 * saisie à refuser : le serveur retire alors l'alias, et les autres écrans
 * retombent sur « Sans nom », qui est vrai.
 */
function AliasField(): JSX.Element {
  const saved = me.value?.alias ?? '';
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const value = draft ?? saved;
  const dirty = value.trim() !== saved.trim();

  const save = (): void => {
    if (!dirty) return;
    setDraft(undefined);
    void onSaveAlias(value);
  };

  return (
    <div class="inline-form">
      <input
        type="text"
        class="input"
        data-testid="alias-input"
        aria-label={t('me.alias')}
        placeholder={t('me.aliasPlaceholder')}
        value={value}
        onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if ((e as KeyboardEvent).key === 'Enter') save();
        }}
      />
      {dirty ? (
        <Action testId="save-alias" variant="primary" onClick={save}>
          <IconCheck size={18} />
          <span>{t('me.saveAlias')}</span>
        </Action>
      ) : null}
    </div>
  );
}

/**
 * L'avatar, **et le bouton par-dessus**.
 *
 * Un `<input type="file">` caché derrière un libellé : c'est le seul moyen d'avoir
 * un déclencheur qui ressemble au reste de l'interface, le contrôle natif n'étant
 * pas stylable. Le champ est **réinitialisé après coup** — sans cela, choisir deux
 * fois le même fichier n'émettrait pas de second `change`, et un envoi qu'on
 * refait après une erreur ne partirait pas.
 *
 * `accept` porte les trois formats que le serveur accepte, et pas « image/* » :
 * proposer un HEIC pour le voir refusé au `complete` serait un aller-retour de
 * mégaoctets pour une réponse qu'on connaissait avant de commencer.
 */
function AvatarPicker(): JSX.Element {
  const profile = me.value;

  return (
    <label class="avatar-picker" data-testid="avatar-picker">
      <Avatar
        uid={profile?.uid ?? ''}
        alias={profile?.alias}
        rev={profile?.avatarRev}
        big
        testId="my-avatar"
      />
      <span class="avatar-picker-badge" aria-hidden="true">
        <IconCamera size={14} />
      </span>
      <span class="sr-only">{t('me.changeAvatar')}</span>
      <input
        type="file"
        class="sr-only"
        data-testid="avatar-input"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          const input = e.currentTarget as HTMLInputElement;
          const file = input.files?.[0];
          input.value = '';
          if (file) void onPickAvatar(file);
        }}
      />
    </label>
  );
}
